import React, { useCallback, useState, useMemo, useContext } from "react";
import { debounce } from "lodash";
import axios from "axios";
import useApiEp from "../../hooks/useApiEp";
import TitleActionsContainer from "../../widgets/TitleActionsContainer";
import PageTitle from "../../widgets/PageTitle";
import {
  Button,
  Container,
  Stack,
  TextField,
  Alert,
  Box,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import DataTable from "../../widgets/DataTable";
import { Link, useLocation, useNavigate } from "react-router-dom";
import QueueStatusChip from "./QueueStatusChip";
import CategoryChip from "./CategoryChip";
import { toast } from "react-toastify"; // adjust to your toast lib
import { API_ENDPOINT, USER_TYPE } from "../../utils/constants";
import { getHeader, toastError } from "../../utils/util";
import { UserContext } from "../../context/UserProvider";

const CAN_PAUSE = ["processing"];
const CAN_RESUME = ["paused"];
const CAN_STOP = ["processing", "paused"];

const AllRdCampaignList = () => {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const navigate = useNavigate();
  const rcsType = query.get("type");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sq, setSq] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Stop confirmation dialog state
  const [stopDialog, setStopDialog] = useState({
    open: false,
    campaignId: null,
    campaignName: "",
  });

  // Track which row is currently performing an action (to show loading)
  const [actionLoading, setActionLoading] = useState(null); // `${id}-${action}`

  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/campaign/get-all-campaigns?page=${page}&name=${searchQuery}&type=${rcsType}`
  );
  const { user, loading } = useContext(UserContext);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchQuery(value);
        setPage(1);
      }, 500),
    []
  );

  const handleSearchChange = (e) => {
    setSq(e.target.value);
    debouncedSearch(e.target.value);
  };

  React.useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  // Core API call
  const callCampaignAction = useCallback(
    async (campaignId, action) => {
      const loadingKey = `${campaignId}-${action}`;
      setActionLoading(loadingKey);
      try {
        const response = await axios.post(
          `${API_ENDPOINT}/rcs/rcs-campaign-action/${campaignId}`,
          { action, type: rcsType || "" },
          getHeader()
        );
        refetch?.();
        toast.success(response.data.message);
      } catch (err) {
        console.error("Campaign action failed:", err);
        toastError(err);
      } finally {
        setActionLoading(null);
      }
    },
    [rcsType, refetch]
  );

  const handlePause = useCallback(
    (id) => callCampaignAction(id, "pause"),
    [callCampaignAction]
  );

  const handleResume = useCallback(
    (id) => callCampaignAction(id, "resume"),
    [callCampaignAction]
  );

  const handleStopClick = useCallback((id, name) => {
    setStopDialog({ open: true, campaignId: id, campaignName: name });
  }, []);

  const handleStopConfirm = useCallback(async () => {
    const { campaignId } = stopDialog;
    setStopDialog((prev) => ({ ...prev, open: false }));
    await callCampaignAction(campaignId, "stop");
  }, [stopDialog, callCampaignAction]);

  const handleStopCancel = useCallback(() => {
    setStopDialog({ open: false, campaignId: null, campaignName: "" });
  }, []);

  const handleAction = useCallback(
    (action, id) => {
      if (action === "view") {
        navigate(`/home/${id}/campaign-report?type=${rcsType}`);
      }
    },
    [navigate, rcsType]
  );

  // Render action buttons per row based on status
  const renderActions = useCallback(
    (_v, row) => {
      const id = row._id ?? row.id;
      const status = row.status?.toLowerCase();

      return (
        <Box display="flex" gap={0.5} flexWrap="wrap">
          {/* View — always available */}
          <Tooltip title="View">
            <IconButton size="small" onClick={() => handleAction("view", id)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Pause — only when processing */}
          {CAN_PAUSE.includes(status) && (
            <Tooltip title="Pause">
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => handlePause(id)}
                  disabled={actionLoading === `${id}-pause`}
                >
                  {actionLoading === `${id}-pause` ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PauseIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* Resume — only when paused */}
          {CAN_RESUME.includes(status) && (
            <Tooltip title="Resume">
              <span>
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleResume(id)}
                  disabled={actionLoading === `${id}-resume`}
                >
                  {actionLoading === `${id}-resume` ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PlayArrowIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* Stop — only for processing/paused */}
          {CAN_STOP.includes(status) && (
            <Tooltip title="Stop">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleStopClick(id, row.campaignName)}
                  disabled={actionLoading === `${id}-stop`}
                >
                  {actionLoading === `${id}-stop` ? (
                    <CircularProgress size={16} color="error" />
                  ) : (
                    <StopIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      );
    },
    [actionLoading, handleAction, handlePause, handleResume, handleStopClick]
  );

  return (
    <Container sx={{ mt: 3 }}>
      <TitleActionsContainer>
        <PageTitle>All Campaigns List</PageTitle>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: { xs: "100%", sm: "auto" }, mt: { xs: 1, sm: 0 } }}
        >
          <TextField
            size="small"
            value={sq}
            onChange={handleSearchChange}
            placeholder="Search Campaign"
            InputProps={{ startAdornment: <SearchIcon fontSize="small" /> }}
          />
        </Stack>
      </TitleActionsContainer>

      <Alert severity="info">Failed messages are retried 3 times</Alert>

      <DataTable
        sx={{ mb: 1 }}
        columns={[
          {
            id: "serial",
            label: "S.No.",
            render: (_v, _row, idx) => (page - 1) * limit + idx + 1,
          },

          // 👇 conditionally add column
          ...(user?.userType === USER_TYPE.ROOT ||
          user?.userType === USER_TYPE.SUPER_ADMIN
            ? [
                {
                  id: "customerId",
                  label: "Customer Id",
                  render: (value) => (
                    <Link
                      to={`/home/${value}/account-details`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ fontWeight: 500 }}
                    >
                      {value}
                    </Link>
                  ),
                },
              ]
            : []),

          { id: "campaignName", label: "Campaign" },
          { id: "totalContacts", label: "Total Contacts" },

          {
            id: "completionPercentage",
            label: "Completion %",
            render: (value) => (
              <span
                style={{
                  color:
                    value >= 80
                      ? "#4CAF50"
                      : value >= 50
                      ? "#FFC107"
                      : "#F44336",
                  fontWeight: "bold",
                }}
              >
                {value}%
              </span>
            ),
          },

          {
            id: "status",
            label: "Status",
            render: (v) => <QueueStatusChip status={v} />,
          },

          {
            id: "category",
            label: "Category",
            render: (_v, row) => (
              <Box display="flex" flexDirection="column">
                <CategoryChip category={row.category} />
                {row.category?.toLowerCase() === "scheduled" &&
                  row.scheduledDate && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 0.5, color: "text.secondary" }}
                    >
                      {new Date(row.scheduledDate).toLocaleString("en-IN", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </Typography>
                  )}
              </Box>
            ),
          },

          {
            id: "createdOn",
            label: "Created On",
            render: (v) =>
              new Date(v).toLocaleString("en-IN", {
                dateStyle: "medium",
                timeStyle: "short",
              }),
          },

          {
            id: "_actions",
            label: "Actions",
            render: renderActions,
          },
        ]}
        actions={[]}
        rows={data?.data ?? []}
        page={page}
        limit={limit}
        totalPages={data?.totalPages ?? 1}
        onPageChange={setPage}
        onAction={handleAction}
      />

      {/* Stop confirmation dialog */}
      <Dialog
        open={stopDialog.open}
        onClose={handleStopCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Stop campaign?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to permanently stop{" "}
            <strong>"{stopDialog.campaignName}"</strong>. This will drain the
            queue and cannot be undone — a stopped campaign cannot be resumed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStopCancel}>Cancel</Button>
          <Button
            onClick={handleStopConfirm}
            color="error"
            variant="contained"
            disableElevation
          >
            Yes, stop campaign
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AllRdCampaignList;
